import { isUnknownArray } from "../types/data";
export const SUPPORTED_LANGUAGES = ["ru", "en"] as const;
export type Language = typeof SUPPORTED_LANGUAGES[number];
export type LanguageSource = "manual" | "profile" | "browser" | "legacy";
export interface LanguagePreference { language: Language; source: LanguageSource }
export const DEFAULT_LANGUAGE = "en";
export const LANGUAGE_STORAGE_KEY = "cineparagon_language";
export const LANGUAGE_SOURCE_STORAGE_KEY = "cineparagon_language_source";

export const LANGUAGE_SOURCES = Object.freeze({
  MANUAL: "manual",
  PROFILE: "profile",
  BROWSER: "browser",
  LEGACY: "legacy"
});

const STORED_LANGUAGE_SOURCES = new Set<string>(Object.values(LANGUAGE_SOURCES));
let memoryPreference: LanguagePreference | null = null;
let memoryPreferenceIsAuthoritative = false;

export const getSupportedLanguage = (value: unknown): Language | null => {
  if (typeof value !== "string") return null;
  const primaryLanguage = value.trim().toLowerCase().split(/[-_]/)[0];
  return primaryLanguage === "ru" || primaryLanguage === "en" ? primaryLanguage : null;
};

export const normalizeLanguage = (value: unknown): Language =>
  getSupportedLanguage(value) || DEFAULT_LANGUAGE;

export const getBrowserLanguage = (browserLanguages?: readonly string[]) => {
  let candidates = browserLanguages;

  if (!isUnknownArray(candidates)) {
    if (typeof navigator === "undefined") return DEFAULT_LANGUAGE;
    candidates = isUnknownArray(navigator.languages) && navigator.languages.length
      ? navigator.languages
      : [navigator.language];
  }

  for (const candidate of candidates) {
    const supportedLanguage = getSupportedLanguage(candidate);
    if (supportedLanguage) return supportedLanguage;
  }

  return DEFAULT_LANGUAGE;
};

const isLanguageSource = (value: unknown): value is LanguageSource =>
  typeof value === "string" && STORED_LANGUAGE_SOURCES.has(value);

const readLocalStoragePreference = (): { available: boolean; preference: LanguagePreference | null } => {
  if (typeof window === "undefined") {
    return { available: false, preference: null };
  }

  try {
    const language = getSupportedLanguage(
      window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
    );
    if (!language) return { available: true, preference: null };

    const storedSource = window.localStorage.getItem(LANGUAGE_SOURCE_STORAGE_KEY);
    return {
      available: true,
      preference: {
        language,
        source: isLanguageSource(storedSource)
          ? storedSource
          : LANGUAGE_SOURCES.LEGACY
      }
    };
  } catch {
    return { available: false, preference: null };
  }
};

export const getStoredLanguagePreference = (): LanguagePreference | null => {
  if (memoryPreferenceIsAuthoritative && memoryPreference) {
    return memoryPreference;
  }
  const stored = readLocalStoragePreference();
  return stored.available ? stored.preference : memoryPreference;
};

export const setStoredLanguage = (language: unknown, source?: unknown): LanguagePreference => {
  const normalizedLanguage = normalizeLanguage(language);
  const previousPreference = getStoredLanguagePreference();
  const normalizedSource = isLanguageSource(source)
    ? source
    : previousPreference?.source || LANGUAGE_SOURCES.LEGACY;
  const preference = {
    language: normalizedLanguage,
    source: normalizedSource
  };

  memoryPreference = preference;

  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, normalizedLanguage);
      window.localStorage.setItem(LANGUAGE_SOURCE_STORAGE_KEY, normalizedSource);
      memoryPreferenceIsAuthoritative = false;
    } catch {
      // The in-memory preference still keeps manual selection authoritative
      // during this session when localStorage is blocked or full.
      memoryPreferenceIsAuthoritative = true;
    }
  } else {
    memoryPreferenceIsAuthoritative = true;
  }

  return preference;
};

export const getInitialLanguagePreference = () => {
  const storedPreference = getStoredLanguagePreference();
  if (storedPreference) return storedPreference;

  return setStoredLanguage(
    getBrowserLanguage(),
    LANGUAGE_SOURCES.BROWSER
  );
};

export const getStoredLanguage = () =>
  getInitialLanguagePreference().language;

export const hasManualLanguagePreference = () =>
  getInitialLanguagePreference().source === LANGUAGE_SOURCES.MANUAL;

export const applyDocumentLanguage = (language: unknown): Language => {
  const normalizedLanguage = normalizeLanguage(language);
  if (typeof document !== "undefined") {
    document.documentElement.lang = normalizedLanguage;
  }
  return normalizedLanguage;
};

export const getTmdbLanguage = (language: unknown = getStoredLanguage()) =>
  normalizeLanguage(language) === "en" ? "en-US" : "ru-RU";

export const getTmdbImageLanguages = (language: unknown = getStoredLanguage()) =>
  normalizeLanguage(language) === "en" ? "en,null" : "ru,null,en";

export const getIntlLocale = (language: unknown = getStoredLanguage()) =>
  normalizeLanguage(language) === "en" ? "en-US" : "ru-RU";

export const withTmdbLanguageParams = (url: string | URL, language: unknown = getStoredLanguage()) => {
  const nextUrl = typeof url === "string" ? new URL(url) : url;
  nextUrl.searchParams.set("language", getTmdbLanguage(language));
  nextUrl.searchParams.set("include_image_language", getTmdbImageLanguages(language));
  return nextUrl;
};
